import type { IDBPDatabase } from "idb";
import { openDB } from "idb";
import * as logger from "./logger";
import { Domain } from "./logger";

const dbName = "phenix-field";
const dbVersion = 1;
const ttlInDays = 30;

let dbInstance: IDBPDatabase<unknown> | null = null;

interface storageOptions {
  storeName: string;
  taskId?: number;
}

interface imagesField {
  kind: "images";
  uploaded: string[];
  to_upload: string[] | null;
  nb_files_allowed: number | null;
  to_upload_length?: number;
}

function isImagesField(x: unknown): x is imagesField {
  return (
    !!x &&
    typeof x === "object" &&
    "kind" in x &&
    x.kind === "images" &&
    "uploaded" in x &&
    "to_upload" in x
  );
}

async function openDatabase() {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB(dbName, dbVersion, {
    upgrade(db) {
      createStores(db);
    },
  });
  await ensureDefaults(dbInstance);

  return dbInstance;
}

async function ensureDefaults(db: IDBPDatabase<unknown>) {
  const existing = await db.get("main", "offlineMode");
  if (existing === undefined) {
    await db.put("main", { key: "offlineMode", value: false });
  }
}

function createStores(db: IDBPDatabase<unknown>) {
  // Create table tasks
  if (!db.objectStoreNames.contains("tasks")) {
    db.createObjectStore("tasks", { keyPath: "id" });
  }
  // Create table forms
  if (!db.objectStoreNames.contains("forms")) {
    db.createObjectStore("forms", { keyPath: "key" });
  }
  // Create table assets
  if (!db.objectStoreNames.contains("assets")) {
    db.createObjectStore("assets", { keyPath: "path" });
  }
  // Create table default
  if (!db.objectStoreNames.contains("main")) {
    db.createObjectStore("main", { keyPath: "key" });
  }
  // Create table expiration_data
  if (!db.objectStoreNames.contains("expiration_data")) {
    db.createObjectStore("expiration_data", { keyPath: "id" });
  }
  // Create table alpine_persist
  if (!db.objectStoreNames.contains("alpine_persist")) {
    const store = db.createObjectStore("alpine_persist", { keyPath: ["task_id", "uuid"] });
    store.createIndex("task_id", "task_id");
  }
}

async function getItem(key: string, opts: storageOptions): Promise<unknown> {
  const db = await openDatabase();
  const value = await db.get(opts.storeName, key);
  return value === undefined ? null : value;
}

async function hasItem(key: string, opts: storageOptions): Promise<boolean> {
  const db = await openDatabase();
  const value = await db.get(opts.storeName, key);
  return value !== undefined && value !== null;
}

async function keys(opts: storageOptions): Promise<string[]> {
  const db = await openDatabase();
  const keys = await db.getAllKeys(opts.storeName);
  return keys.map(String);
}
async function indexKeys(
  indexName: string,
  indexValue: string | number,
  opts: storageOptions,
): Promise<IDBValidKey[]> {
  const db = await openDatabase();
  const keys = await db.getAllKeysFromIndex(opts.storeName, indexName, indexValue);
  return keys;
}

async function setItem(value: unknown, opts: storageOptions): Promise<unknown> {
  const db = await openDatabase();
  const test = await db.put(opts.storeName, JSON.parse(JSON.stringify(value)));
  return test;
}

async function removeItem(key: string, opts: storageOptions): Promise<void> {
  const db = await openDatabase();
  await db.delete(opts.storeName, key);
}

async function iterate(
  opts: storageOptions,
  callback: (value: unknown, key: string) => void,
): Promise<void> {
  const db = await openDatabase();
  const tx = db.transaction(opts.storeName);
  for await (const cursor of tx.store) {
    callback(cursor.value, String(cursor.key));
  }
}

async function deleteFormData(taskId: number) {
  const db = await openDatabase();
  const index = db.transaction("alpine_persist", "readwrite").store.index("task_id");
  for await (const cursor of index.iterate(taskId)) {
    cursor.delete();
  }
}

async function deleteEmptyFormData(taskId: number) {
  const db = await openDatabase();
  const index = db.transaction("alpine_persist", "readwrite").store.index("task_id");
  for await (const cursor of index.iterate(taskId)) {
    const value = cursor.value.value;
    if (Array.isArray(value) && value.length === 0) cursor.delete();
    if (typeof value === "string" && value.length === 0) cursor.delete();
    if (typeof value === "object" && value.to_upload.length === 0 && value.uploaded.length === 0) {
      cursor.delete();
    }
  }
}

async function listStores(): Promise<string[]> {
  const db = await openDatabase();
  return Array.from(db.objectStoreNames);
}

async function clearExpiredNamespaces() {
  const db = await openDatabase();
  const tx = db.transaction("expiration_data", "readwrite");
  for await (const cursor of tx.store) {
    const taskId = cursor.value.id;
    const lastUsedAt = cursor.value.lastUsedAt;
    if (Date.parse(lastUsedAt) < Date.now() - ttlInDays * 24 * 60 * 60 * 1000) {
      await deleteFormData(taskId);
      await cursor.delete();
      logger.debug(Domain.Storage, `removed expired values task-${taskId})`);
    }
  }
}

async function dumpStorage(): Promise<unknown> {
  const stores = await listStores();

  const dbDump = new Map<string, unknown>();
  const storeDumps = new Map<string, unknown>();
  for (const storeName of stores) {
    const storageDump = new Map<string, unknown>();
    await iterate({ storeName: storeName }, (value: unknown, key: string) => {
      storageDump.set(key, dropImagesAndTruncateLongValues(value));
    });
    storeDumps.set(storeName, Object.fromEntries(storageDump));
  }
  dbDump.set(dbName, Object.fromEntries(storeDumps));
  return Object.fromEntries(dbDump);
}

function dropImagesAndTruncateLongValues(obj: unknown): unknown {
  if (obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => dropImagesAndTruncateLongValues(item));
  }
  if (typeof obj === "string") {
    return obj.substring(0, 200);
  }
  if (typeof obj === "object") {
    if (isImagesField(obj)) {
      obj.to_upload_length = obj.to_upload?.length ?? 0;
      obj.to_upload = [];
      return obj;
    }
    const result: Record<string, unknown> = {};
    const entries: [string, unknown][] = Object.entries(obj);
    for (const [key, value] of entries) {
      result[key] = dropImagesAndTruncateLongValues(value);
    }
    return result;
  }
  return obj;
}

async function clearAllStores() {
  const db = await openDatabase();
  const storeNames = Array.from(db.objectStoreNames);
  const tx = db.transaction(storeNames, "readwrite");

  for (const storeName of storeNames) {
    tx.objectStore(storeName).clear();
  }

  await tx.done;
}

export default {
  getItem: getItem,
  hasItem: hasItem,
  keys: keys,
  indexKeys: indexKeys,
  setItem: setItem,
  removeItem: removeItem,
  iterate: iterate,
  deleteFormData: deleteFormData,
  deleteEmptyFormData: deleteEmptyFormData,
  clearExpiredNamespaces: clearExpiredNamespaces,
  dumpStorage: dumpStorage,
  clearAllStores: clearAllStores,
};
